import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { GroupMemberDto, ResponseDto } from "@mimber/schema"
import { groupMembersKeys } from "../group-members.utils"
import { memberGroupsKeys } from "../../member-groups/member-groups.utils"

export interface RemoveGroupMemberVariables {
  groupUuid: string
  memberUuid: string
}

type RemoveGroupMemberMutationVariables = string | RemoveGroupMemberVariables

interface RemoveGroupMemberOptions extends Omit<
  UseMutationOptions<
    ResponseDto<GroupMemberDto>,
    Error,
    RemoveGroupMemberMutationVariables
  >,
  "mutationFn"
> {}

export const useRemoveGroupMember = (
  groupUuid?: string,
  options: RemoveGroupMemberOptions = {}
): UseMutationResult<
  ResponseDto<GroupMemberDto>,
  Error,
  RemoveGroupMemberMutationVariables
> => {
  guardAppUsage("mimber-organization", "useRemoveGroupMember")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (variables: RemoveGroupMemberMutationVariables) => {
      const resolvedGroupUuid =
        typeof variables === "string" ? groupUuid : variables.groupUuid
      const resolvedMemberUuid =
        typeof variables === "string" ? variables : variables.memberUuid

      if (!resolvedGroupUuid) {
        throw new Error("groupUuid is required to remove a group member")
      }

      const response = await server.delete<ResponseDto<GroupMemberDto>>(
        `groups/${resolvedGroupUuid}/members/${resolvedMemberUuid}`
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      const resolvedGroupUuid =
        typeof variables === "string" ? groupUuid : variables.groupUuid
      const resolvedMemberUuid =
        typeof variables === "string" ? variables : variables.memberUuid

      if (!resolvedGroupUuid) {
        return
      }

      queryClient.invalidateQueries({
        queryKey: groupMembersKeys.findAll(resolvedGroupUuid),
      })
      queryClient.removeQueries({
        queryKey: groupMembersKeys.findOne(
          resolvedGroupUuid,
          resolvedMemberUuid
        ),
      })
      queryClient.invalidateQueries({
        queryKey: memberGroupsKeys.findMemberGroups(resolvedMemberUuid),
      })
      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
